/**
 * StartingMembersGrid — a 3×2 grid of starter Staff Profiles to add.
 *
 * Used by:
 *   - EmptyDashboard:      all 6 profiles
 *   - OnboardingDashboard: profiles filtered by `filterAlreadyAdded`
 *
 * Filtering is exposed as a pure helper so the empty-state copy can be picked
 * independently and the call sites stay declarative.
 *
 * Each card links into the Add wizard with the profile preselected
 * (AddMemberWizard reads `location.state.preselectStaffProfileId` and jumps
 * straight to the Identity step).
 */

import { Link } from 'react-router-dom';
import { Card } from '@/components/Card/Card';
import { Avatar } from '@/components/Avatar/Avatar';
import { Chip } from '@/components/Chip/Chip';
import { t } from '@/lib/i18n';
import { formatPersonName } from '@/lib/format';
import type { StaffProfile } from '@/features/members/schemas';
import styles from './StartingMembersGrid.module.css';

const MAX_VISIBLE = 6;

export interface StartingMembersGridProps {
  profiles: StaffProfile[];
  /** Optional id→name lookups. When supplied, the card shows position + unit chips. */
  positionsById?: Map<string, string>;
  unitsById?: Map<string, string>;
}

interface MemberWithPositions {
  positions: { id: string }[];
}

/**
 * Remove profiles whose default positions are entirely already represented
 * in the team. A profile with no default positions is always kept (no
 * "redundant" claim to filter on).
 */
export function filterAlreadyAdded<T extends { defaultPositionIds: string[] }>(
  profiles: T[],
  members: MemberWithPositions[],
): T[] {
  const addedPositionIds = new Set(members.flatMap((m) => m.positions.map((p) => p.id)));
  return profiles.filter(
    (p) =>
      p.defaultPositionIds.length === 0 ||
      p.defaultPositionIds.some((id) => !addedPositionIds.has(id)),
  );
}

export function StartingMembersGrid({
  profiles,
  positionsById,
  unitsById,
}: StartingMembersGridProps) {
  const visible = profiles.slice(0, MAX_VISIBLE);

  return (
    <section className={styles.section}>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>{t('Common starting Members')}</h2>
        <span className={styles.sectionEyebrow}>{t('pick one to add')}</span>
      </div>
      {visible.length === 0 ? (
        <p className={styles.emptyLine}>
          {t("You've already added someone to every starter role — nice work.")}
        </p>
      ) : (
        <div className={styles.grid}>
          {visible.map((profile) => {
            const name = formatPersonName(profile);
            const positionNames =
              positionsById === undefined
                ? []
                : profile.defaultPositionIds
                    .map((id) => positionsById.get(id))
                    .filter((n): n is string => Boolean(n));
            const unitNames =
              unitsById === undefined
                ? []
                : profile.defaultUnitIds
                    .map((id) => unitsById.get(id))
                    .filter((n): n is string => Boolean(n));
            const showChips = positionNames.length > 0 || unitNames.length > 0;

            return (
              <Link
                key={profile.id}
                to="/team/add"
                state={{ preselectStaffProfileId: profile.id }}
                className={styles.cardLink}
              >
                <Card padding="md" className={styles.card}>
                  <Avatar name={name} size="lg" kind="ai" />
                  <div className={styles.text}>
                    <p className={styles.name}>{name}</p>
                    {showChips ? (
                      <div className={styles.chips}>
                        {positionNames.map((n) => (
                          <Chip key={`p-${n}`} variant="neutral" size="sm">
                            {n}
                          </Chip>
                        ))}
                        {unitNames.map((n) => (
                          <Chip key={`u-${n}`} variant="neutral" size="sm">
                            {n}
                          </Chip>
                        ))}
                      </div>
                    ) : null}
                    {profile.description ? (
                      <p className={styles.description}>{profile.description}</p>
                    ) : null}
                    <span className={styles.addCta}>{t('Add →')}</span>
                  </div>
                </Card>
              </Link>
            );
          })}
        </div>
      )}
    </section>
  );
}
